import * as React from 'react';
import { ContainerType } from '../ContainerComponents/ContainerPage';
import { centsToPrice } from '@/utils/formatter';
import { Currency, CurrencyIdByString } from '@/utils/price';
import { Table } from 'reactstrap';
import { CustomTable } from '../../components/Table';
import i18n from '@/utils/i18n';
import './ContainerPrices.css';

const ContainerPart = {
  Complete: 'StoreLostCost',
  Upper: 'StoreLostCostUpper',
  Lower: 'StoreLostCostLower',
};

/**
 * Default container prices for stores
 */
export type StoreDefaultContainerPrice = { Id: number } & {
  [key: string]: {
    TransactionPrice: string;
    StoreLostCost: string;
    StoreLostCostUpper: string;
    StoreLostCostLower: string;
    RentalPrice: string;
    CleaningPrice: string;
    LostPrice: string;
    DepositPrice: string;
    CostPrice: string;
    LogisticPartnerCleaningPrice: string;
  };
};

/**
 * Container overdue price for consumers
 */
export interface ContainerOverduePrice {
  containerTypeId: number;
  currency: number;
  priceInclVatInCents: string;
  creditsOnReturnInCents: string;
}

export type SystemType = 'OpenSystem' | 'Event';

type ContainerPriceTableProps = {
  storeDefaultContainerPrices: StoreDefaultContainerPrice[];
  containerTypes: ContainerType[];
  language: string;
  consumerOverduePrices: {
    [currency: string]: { [containerTypeId: number]: ContainerOverduePrice };
  };
  currencyFilter: string[];
  setStoreDefaultContainerPrices: React.Dispatch<
    React.SetStateAction<StoreDefaultContainerPrice[]>
  >;
  setConsumerOverduePrices: React.Dispatch<
    React.SetStateAction<{
      [currency: string]: {
        [containerTypeId: number]: ContainerOverduePrice;
      };
    }>
  >;
  setTouchedContainerTypeIds: React.Dispatch<React.SetStateAction<number[]>>;
  systemType: SystemType;
};

const EventPriceTypes = [
  'RentalPrice',
  'CleaningPrice',
  'LostPrice',
  'DepositPrice',
  'CostPrice',
  'LogisticPartnerCleaningPrice',
] as const;

type EventPriceType = (typeof EventPriceTypes)[number];
export type PiceType = EventPriceType | 'TransactionPrice';

export default function ContainerPriceTable({
  storeDefaultContainerPrices,
  containerTypes,
  consumerOverduePrices,
  currencyFilter,
  setStoreDefaultContainerPrices,
  setConsumerOverduePrices,
  language,
  systemType,
  setTouchedContainerTypeIds,
}: ContainerPriceTableProps) {
  // Memoize filtered prices calculation
  const filteredPrices = React.useMemo(() => {
    const containerTypeIds = new Set(containerTypes.map((ct) => ct.id));
    return storeDefaultContainerPrices.filter((p) =>
      containerTypeIds.has(p.Id),
    );
  }, [storeDefaultContainerPrices, containerTypes]);

  // Memoize filtered currencies
  const filteredCurrencies = React.useMemo(() => {
    return Object.keys(Currency).filter((currency) => {
      if (currencyFilter.length === 0 || currencyFilter.includes(currency)) {
        return true;
      }
      return false;
    });
  }, [currencyFilter]);

  // Memoize container type lookup maps for performance
  const containerTypeMap = React.useMemo(() => {
    const map = new Map<number, ContainerType>();
    containerTypes.forEach((ct) => map.set(ct.id, ct));
    return map;
  }, [containerTypes]);

  const getContainerTypeName = React.useCallback(
    (id: number) => {
      const containerType = containerTypeMap.get(id);
      if (containerType == null) {
        return '';
      }
      if (containerType.namesByLanguage == null) {
        return containerType.name;
      }
      return containerType.namesByLanguage[language];
    },
    [containerTypeMap, language],
  );

  const getContainerTypeSku = React.useCallback(
    (id: number) => {
      const containerType = containerTypeMap.get(id);
      if (containerType == null) {
        return '';
      }
      return containerType.sku;
    },
    [containerTypeMap],
  );

  const setPrice = React.useCallback(
    (priceType: PiceType, id: number, currency: string, value: any) => {
      setTouchedContainerTypeIds((oldIds) => {
        if (!oldIds.includes(id)) {
          return [...oldIds, id];
        }
        return oldIds;
      });

      const newDefaults = [...storeDefaultContainerPrices];
      const newDefaultContainerTypeIndex = newDefaults.findIndex(
        (defaultContainerPrice) => defaultContainerPrice.Id === id,
      );

      newDefaults[newDefaultContainerTypeIndex][currency][priceType] = value;

      setStoreDefaultContainerPrices(newDefaults);
    },
    [
      storeDefaultContainerPrices,
      setStoreDefaultContainerPrices,
      setTouchedContainerTypeIds,
    ],
  );

  const setStoreLostCost = React.useCallback(
    (id: number, currency: string, value: any, part: string) => {
      setTouchedContainerTypeIds((oldIds) => {
        if (!oldIds.includes(id)) {
          return [...oldIds, id];
        }
        return oldIds;
      });

      const newDefaults: any[] = [...storeDefaultContainerPrices];
      const newDefaultContainerTypeIndex = newDefaults.findIndex(
        (defaultContainerPrice) => defaultContainerPrice.Id === id,
      );

      newDefaults[newDefaultContainerTypeIndex][currency][part] = value;

      setStoreDefaultContainerPrices(newDefaults);
    },
    [
      storeDefaultContainerPrices,
      setStoreDefaultContainerPrices,
      setTouchedContainerTypeIds,
    ],
  );

  /**
   * Get the overdue prices a customer has to be for currency
   */
  const getConsumerOverduePrice = React.useCallback(
    (currency: string, containerTypeId: number) => {
      const currencyId = (CurrencyIdByString as any)[currency];
      const overduePricesByContainerTypeId = consumerOverduePrices[currency];
      if (overduePricesByContainerTypeId == null) {
        const res: ContainerOverduePrice = {
          creditsOnReturnInCents: '0',
          priceInclVatInCents: '0',
          containerTypeId: containerTypeId,
          currency: currencyId,
        };
        return res;
      }
      if (overduePricesByContainerTypeId[containerTypeId] == null) {
        // default
        return { ...overduePricesByContainerTypeId[-1] };
      }
      return overduePricesByContainerTypeId[containerTypeId];
    },
    [consumerOverduePrices],
  );

  const setConsumerOverduePrice = React.useCallback(
    (
      currency: string,
      containerTypeId: number,
      attrName: string,
      attrValue: string,
    ) => {
      const overduePrice: any = getConsumerOverduePrice(
        currency,
        containerTypeId,
      );
      overduePrice[attrName] = attrValue;
      overduePrice.containerTypeId = containerTypeId;
      setConsumerOverduePrices((oldPrices) => {
        const newPrices = { ...oldPrices };
        newPrices[currency][overduePrice.containerTypeId] = overduePrice;
        return newPrices;
      });
    },
    [getConsumerOverduePrice, setConsumerOverduePrices],
  );

  // Memoize OpenSystem price rows to avoid recreation on every render
  const getOpenSystemPrices = React.useCallback(
    (containerInfo: StoreDefaultContainerPrice) => {
      return filteredCurrencies.map((currency) => (
        <tr key={`${containerInfo.Id}-${currency}`}>
          <td>
            <div className="input-group">
              <input
                value={containerInfo[currency].TransactionPrice}
                type="text"
                className="form-control"
                onChange={(e) =>
                  setPrice(
                    'TransactionPrice',
                    containerInfo.Id,
                    currency,
                    e.target.value,
                  )
                }
              />
              <span className="input-group-text">
                {' '}
                {centsToPrice(
                  parseFloat(containerInfo[currency].TransactionPrice) * 100,
                  currency,
                  3,
                )}
              </span>
            </div>
          </td>
          <td>
            <div className="input-group">
              <input
                value={
                  getConsumerOverduePrice(currency, containerInfo.Id)
                    .priceInclVatInCents
                }
                type="text"
                className="form-control"
                onChange={(e) =>
                  setConsumerOverduePrice(
                    currency,
                    containerInfo.Id,
                    'priceInclVatInCents',
                    e.target.value,
                  )
                }
              />
              <span className="input-group-text">
                {' '}
                {centsToPrice(
                  getConsumerOverduePrice(currency, containerInfo.Id)
                    .priceInclVatInCents,
                  currency,
                  3,
                )}
              </span>
            </div>
          </td>
          <td>
            <div className="input-group">
              <input
                value={
                  getConsumerOverduePrice(currency, containerInfo.Id)
                    .creditsOnReturnInCents
                }
                type="text"
                className="form-control"
                onChange={(e) =>
                  setConsumerOverduePrice(
                    currency,
                    containerInfo.Id,
                    'creditsOnReturnInCents',
                    e.target.value,
                  )
                }
              />
              <span className="input-group-text">
                {' '}
                {centsToPrice(
                  getConsumerOverduePrice(currency, containerInfo.Id)
                    .creditsOnReturnInCents,
                  currency,
                  3,
                )}
              </span>
            </div>
          </td>
          <td>
            <div className="input-group">
              <input
                value={containerInfo[currency].StoreLostCost}
                type="text"
                className="form-control"
                onChange={(e) =>
                  setStoreLostCost(
                    containerInfo.Id,
                    currency,
                    e.target.value,
                    ContainerPart.Complete,
                  )
                }
              />
              <span className="input-group-text">
                {' '}
                {centsToPrice(
                  parseInt(containerInfo[currency].StoreLostCost),
                  currency,
                  2,
                )}
              </span>
            </div>
          </td>
          <td>
            <div className="input-group">
              <input
                value={containerInfo[currency].StoreLostCostUpper}
                type="text"
                className="form-control"
                onChange={(e) =>
                  setStoreLostCost(
                    containerInfo.Id,
                    currency,
                    e.target.value,
                    ContainerPart.Upper,
                  )
                }
              />
              <span className="input-group-text">
                {' '}
                {centsToPrice(
                  parseInt(containerInfo[currency].StoreLostCostUpper),
                  currency,
                  2,
                )}
              </span>
            </div>
          </td>
          <td>
            <div className="input-group">
              <input
                value={containerInfo[currency].StoreLostCostLower}
                type="text"
                className="form-control"
                onChange={(e) =>
                  setStoreLostCost(
                    containerInfo.Id,
                    currency,
                    e.target.value,
                    ContainerPart.Lower,
                  )
                }
              />
              <span className="input-group-text">
                {' '}
                {centsToPrice(
                  containerInfo[currency].StoreLostCostLower,
                  currency,
                  2,
                )}
              </span>
            </div>
          </td>
        </tr>
      ));
    },
    [
      filteredCurrencies,
      setPrice,
      setStoreLostCost,
      getConsumerOverduePrice,
      setConsumerOverduePrice,
    ],
  );

  // Memoize Event price rows
  const getEventPrices = React.useCallback(
    (containerInfo: StoreDefaultContainerPrice) => {
      return filteredCurrencies.map((currency) => (
        <tr key={`${containerInfo.Id}-${currency}`}>
          {EventPriceTypes.map((priceType) => (
            <td key={`${containerInfo.Id}-${currency}-${priceType}`}>
              <div className="input-group">
                <input
                  value={
                    (containerInfo[currency] as Record<string, string>)[
                      priceType
                    ]
                  }
                  type="text"
                  className="form-control"
                  onChange={(e) =>
                    setPrice(
                      priceType,
                      containerInfo.Id,
                      currency,
                      e.target.value,
                    )
                  }
                />
                <span className="input-group-text">
                  {' '}
                  {centsToPrice(
                    parseFloat(containerInfo[currency][priceType]),
                    currency,
                    3,
                  )}
                </span>
              </div>
            </td>
          ))}
        </tr>
      ));
    },
    [filteredCurrencies, setPrice],
  );

  // Generate table rows
  const tableRows = filteredPrices.map((defaultContainerInfo) => {
    return (
      <tr key={defaultContainerInfo.Id}>
        <td>{defaultContainerInfo.Id}</td>
        <td>{getContainerTypeSku(defaultContainerInfo.Id)}</td>
        <td>{getContainerTypeName(defaultContainerInfo.Id)}</td>
        <td>
          <Table striped bordered>
            <tbody>
              {systemType === 'OpenSystem' &&
                getOpenSystemPrices(defaultContainerInfo)}
              {systemType === 'Event' && getEventPrices(defaultContainerInfo)}
            </tbody>
            <thead>
              {/* This is below tbody on purpose! To have the <th> sticky above the tbody. */}
              {systemType === 'OpenSystem' && (
                <tr>
                  <th className="sticky-table-header">
                    {i18n.t('EditContainerPrices:transactionPrice')}
                  </th>
                  <th className="sticky-table-header">
                    {i18n.t('EditContainerPrices:overdueCost')}
                  </th>
                  <th className="sticky-table-header">
                    {i18n.t('EditContainerPrices:creditsOnOverdueReturn')}
                  </th>
                  <th className="sticky-table-header">
                    {i18n.t('EditContainerPrices:storeLostCost')}
                  </th>
                  <th className="sticky-table-header">
                    {i18n.t('EditContainerPrices:storeLostCostUpper')}
                  </th>
                  <th className="sticky-table-header">
                    {i18n.t('EditContainerPrices:storeLostCostLower')}
                  </th>
                </tr>
              )}
              {systemType === 'Event' && (
                <tr>
                  <th className="sticky-table-header">
                    {i18n.t('EditContainerPrices:netRentalPrice')}
                  </th>
                  <th className="sticky-table-header">
                    {i18n.t('EditContainerPrices:netCleaningPrice')}
                  </th>
                  <th className="sticky-table-header">
                    {i18n.t('EditContainerPrices:netLostPrice')}
                  </th>
                  <th className="sticky-table-header">
                    {i18n.t('EditContainerPrices:netDepositPrice')}
                  </th>
                  <th className="sticky-table-header">
                    {i18n.t('EditContainerPrices:netCostPrice')}
                  </th>
                  <th className="sticky-table-header">
                    {i18n.t(
                      'EditContainerPrices:netLogisticPartnerCleaningPrice',
                    )}
                  </th>
                </tr>
              )}
            </thead>
          </Table>
        </td>
      </tr>
    );
  });

  // Custom header elements with specific column widths
  const headerElements = [
    <th key="id" style={{ width: '5%' }}>
      {i18n.t('EditContainerPrices:id')}
    </th>,
    <th key="sku" style={{ width: '10%' }}>
      {i18n.t('EditContainerPrices:sku')}
    </th>,
    <th key="name" style={{ width: '20%' }}>
      {i18n.t('EditContainerPrices:name')} ({language})
    </th>,
    <th key="prices" style={{ width: '65%' }}>
      {i18n.t('EditContainerPrices:prices')}
    </th>,
  ];

  return (
    <CustomTable
      headers={['Id', 'SKU', `Name (${language})`, 'Prices']}
      rows={tableRows}
      loading={false}
      error={undefined}
      striped={true}
      bordered={true}
      hover={false}
      headerElements={headerElements}
    />
  );
}
